(function () {
  // Global variable for inseam mapping table
  window.inseamMappingTable = null;

  // Tracks last selected Color to detect if `variant:update` was triggered by Color.
  let _mmLastSelectedColorValueId = null;

  /** PDP helper cookie: single source for height + fitSymbol. Set by theme whenever quiz/known-size is completed. */
  var PDP_FIT_COOKIE = '_mm_pdp_fit';

  // Returns whether inseam recommendation should run on this product page.
  function getProductTypeFromDom() {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) return '';
    const t = variantPicker.getAttribute('data-product-type') || '';
    return String(t || '').trim().toLowerCase();
  }

  // Returns true if inseam recommendations should run on this PDP.
  function isInseamFeatureEnabledOnThisProduct() {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (variantPicker?.getAttribute('data-mm-skip-inseam') === 'true') {
      variantPicker.classList.remove('variant-picker--loading');
    }

    const productType = getProductTypeFromDom();
    const defaultDisabled = [
      'tops',
      'top',
      'tumbler',
      'tumblers',
      'gift cards',
      'gift card',
      'gift-cards',
    ];
    const disabledList = defaultDisabled;

    if (!productType) return true;

    const enabled = disabledList.indexOf(productType) === -1;
    if (!enabled) {
      if (variantPicker) {
        variantPicker.classList.remove('variant-picker--loading');
      }
    }
    return enabled;
  }

  /**
   * Read height and fitSymbol from the PDP helper cookie.
   * @returns {{ height: string, fitSymbol: string } | null}
   */
  function getPdpFit() {
    if (typeof Cookies === 'undefined') return null;
    try {
      const raw = Cookies.get(PDP_FIT_COOKIE);
      if (!raw) return null;
      const data = JSON.parse(raw);
      const height = data.height != null && String(data.height).trim() !== '' ? String(data.height).trim() : null;
      const fitSymbol = data.fitSymbol != null && String(data.fitSymbol).trim() !== '' ? String(data.fitSymbol).trim() : null;
      if (height && fitSymbol) return { height: height, fitSymbol: fitSymbol };
    } catch (e) {
      // ignore
    }
    return null;
  }

  /**
   * Get user height. Prefers _mm_pdp_fit; falls back to legacy _mm_quiz_data / _mm_reshop_height.
   */
  function getUserHeight() {
    const pdp = getPdpFit();
    if (pdp && pdp.height) return pdp.height;
    if (typeof Cookies === 'undefined') return null;
    try {
      const quizDataCookie = Cookies.get('_mm_quiz_data');
      if (quizDataCookie) {
        const quizData = JSON.parse(quizDataCookie);
        const h = quizData.height != null && String(quizData.height).trim() !== '' ? quizData.height : null;
        if (h) return h;
      }
    } catch (e) {
      console.error('[PDP Quiz State] Error parsing quiz data:', e);
    }
    // Fallback to reshop height
    return Cookies.get('_mm_reshop_height') || null;
  }

  /**
   * Get user fit symbol. Prefers _mm_pdp_fit; falls back to legacy _mm_quiz_data / mm_fitsymbol.
   */
  function getUserFitSymbol() {
    const pdp = getPdpFit();
    if (pdp && pdp.fitSymbol) return pdp.fitSymbol;
    if (typeof Cookies === 'undefined') return null;
    try {
      const quizDataCookie = Cookies.get('_mm_quiz_data');
      if (quizDataCookie) {
        const quizData = JSON.parse(quizDataCookie);
        if (quizData.fitSymbol) return quizData.fitSymbol;
      }
    } catch (e) {
      console.error('[PDP Quiz State] Error parsing quiz data:', e);
    }
    return Cookies.get('mm_fitsymbol') || null;
  }

  /** True when height is a specific value like 5'3 (not NA or legacy range). */
  function hasActualHeight(height) {
    if (!height) return false;
    const h = String(height).trim();
    if (!h || h.toUpperCase() === 'NA') return false;
    return /^\d+'\d+/.test(h);
  }

  /** Klaviyo legacy / email fh=NA users who have size but no actual height. */
  function userNeedsHeightRetake() {
    if (!getUserFitSymbol()) return false;
    if (typeof Cookies !== 'undefined' && Cookies.get('_mm_needs_height') === '1') {
      return true;
    }
    return !hasActualHeight(getUserHeight());
  }

  /**
   * Load inseam mapping table from DOM
   * This must be called after DOM morphing to get fresh reference
   */
  function loadInseamMappingTable() {
    const mappingTableScript = document.getElementById('inseam-mapping-table');
    if (!mappingTableScript) {
      console.warn('[PDP Quiz State] Inseam mapping table script element not found');
      return false;
    }

    try {
      window.inseamMappingTable = JSON.parse(mappingTableScript.textContent);
      return true;
    } catch (e) {
      console.error('[PDP Quiz State] Error parsing inseam mapping table:', e);
      return false;
    }
  }

  /**
   * Normalize height string for comparison
   * Handles different formats like "5'2", "5' 2"", "5'2\"" etc.
   */
  function normalizeHeight(height) {
    if (!height) return '';
    return height.toString()
      .replace(/['"]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase()
      .trim();
  }

  /**
   * Normalize inseam value for comparison
   */
  function normalizeInseamForCompare(val) {
    if (val == null) return '';
    return String(val).trim().replace(/"\s*$/, '').trim();
  }

  /**
   * Coerce a variant ID to a numeric ID for mapping-table lookup.
   * Accepts numeric IDs (number or numeric string) and GraphQL global IDs (gid://.../123).
   * @param {string|number|null|undefined} variantId
   * @returns {number|null}
   */
  function coerceVariantIdToNumber(variantId) {
    if (variantId == null) return null;
    if (typeof variantId === 'number' && !Number.isNaN(variantId)) return variantId;
    const str = String(variantId).trim();
    if (!str) return null;
    const match = str.match(/(\d+)(?!.*\d)/); // last number in the string
    if (!match) return null;
    const num = parseInt(match[1], 10);
    return Number.isNaN(num) ? null : num;
  }

  /**
   * Bind inseam legend tooltip (desktop hover + mobile tap).
   * Mobile: defers outside-click close so the same tap does not immediately dismiss the tooltip.
   */
  function bindInseamLegendTooltip(wrapper, options) {
    if (!wrapper) return;

    const boundAttr = options.boundAttr || 'data-inseam-tooltip-bound';
    if (wrapper.getAttribute(boundAttr) === 'true') return;

    const tooltip = wrapper.querySelector('.variant-option__not-recommended-tooltip');
    const iconTrigger = wrapper.querySelector(options.triggerSelector);
    const closeBtn = wrapper.querySelector('.variant-option__not-recommended-tooltip__close');
    if (!tooltip || !iconTrigger) return;

    wrapper.setAttribute(boundAttr, 'true');
    iconTrigger.setAttribute('role', 'button');

    let suppressOutsideCloseUntil = 0;

    function showTooltip() {
      tooltip.classList.add('variant-option__not-recommended-tooltip--visible');
      tooltip.setAttribute('aria-hidden', 'false');
    }

    function hideTooltip() {
      tooltip.classList.remove('variant-option__not-recommended-tooltip--visible');
      tooltip.setAttribute('aria-hidden', 'true');
    }

    function toggleTooltip() {
      if (tooltip.classList.contains('variant-option__not-recommended-tooltip--visible')) {
        hideTooltip();
      } else {
        showTooltip();
      }
    }

    function onTriggerActivate(e) {
      e.preventDefault();
      e.stopPropagation();
      suppressOutsideCloseUntil = Date.now() + 350;
      toggleTooltip();
    }

    iconTrigger.addEventListener('click', onTriggerActivate);
    iconTrigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        onTriggerActivate(e);
      }
    });

    iconTrigger.addEventListener('focus', showTooltip);
    wrapper.addEventListener('focusout', function (e) {
      if (!wrapper.contains(e.relatedTarget)) hideTooltip();
    });
    wrapper.addEventListener('mouseenter', showTooltip);
    wrapper.addEventListener('mouseleave', hideTooltip);

    document.addEventListener('click', function (e) {
      if (Date.now() < suppressOutsideCloseUntil) return;
      if (!wrapper.contains(e.target) && tooltip.classList.contains('variant-option__not-recommended-tooltip--visible')) {
        hideTooltip();
      }
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        hideTooltip();
      });
    }

    wrapper.addEventListener('click', function (e) {
      if (e.target.closest('.variant-option__not-recommended-tooltip__close')) {
        e.preventDefault();
        e.stopPropagation();
        hideTooltip();
        if (typeof iconTrigger.focus === 'function') iconTrigger.focus();
        return;
      }
      if (typeof options.onWrapperClick === 'function') {
        options.onWrapperClick(e);
      }
    });
  }

  /**
   * One-time setup for "Not recommended" tooltip:
   * show on hover, focus, or tap of the info icon; hide on leave, blur, outside tap, or close.
   */
  function setupNotRecommendedTooltip(wrapper) {
    bindInseamLegendTooltip(wrapper, {
      boundAttr: 'data-inseam-tooltip-bound',
      triggerSelector: '[data-inseam-not-recommended-trigger]',
    });
  }

  /**
   * One-time setup for retake-quiz tooltip on inseam legend (legacy Klaviyo users).
   */
  function setupRetakeQuizTooltip(wrapper) {
    bindInseamLegendTooltip(wrapper, {
      boundAttr: 'data-inseam-retake-bound',
      triggerSelector: '[data-inseam-retake-trigger]',
      onWrapperClick: function (e) {
        const link = e.target.closest('.js-shapelogic-start');
        if (link) {
          try {
            localStorage.setItem('pendingProductUrl', window.location.href);
          } catch (err) { /* ignore */ }
        }
      },
    });
  }

  function showRetakeQuizUI() {
    const retakeEl = document.querySelector('[data-inseam-retake-quiz]');
    if (retakeEl) {
      retakeEl.classList.remove('visually-hidden');
      const wrapper = retakeEl.querySelector('[data-inseam-retake-wrapper]');
      if (wrapper) setupRetakeQuizTooltip(wrapper);
    }
    const notRecommendedEl = document.querySelector('[data-inseam-not-recommended]');
    if (notRecommendedEl) notRecommendedEl.classList.add('visually-hidden');
    document.querySelectorAll('.variant-option__legend-key[data-variant-option-key]').forEach(function (legend) {
      const key = legend.getAttribute('data-variant-option-key');
      if (key && key.toLowerCase().includes('inseam')) {
        legend.textContent = 'Inseam:';
        legend.setAttribute('data-variant-option-key', 'Inseam:');
      }
    });
  }

  function hideRetakeQuizUI() {
    const retakeEl = document.querySelector('[data-inseam-retake-quiz]');
    if (retakeEl) retakeEl.classList.add('visually-hidden');
  }

  /** Fit symbol present but no actual height (e.g. Klaviyo fh=NA). */
  function hasPartialFitData() {
    return Boolean(getUserFitSymbol() && !hasActualHeight(getUserHeight()));
  }

  /**
   * Restore inseam picker + retake UI after variant morph for partial-fit users.
   * Morph replaces the picker with server HTML where inseam is visually-hidden again.
   */
  function applyPartialFitSymbolUI() {
    showQuizEnabledUI();
    if (userNeedsHeightRetake()) {
      showRetakeQuizUI();
    } else {
      hideRetakeQuizUI();
    }
  }

  /**
   * Update inseam recommendation based on current variant and user height
   * IMPORTANT: This function re-queries DOM elements every time to handle morphing
   * @param {number} variantId - The variant ID to match
   * @param {string} userHeight - User's height from quiz
   * @param {boolean} [allowAutoSelect=true] - If true, auto-select recommended inseam; if false, only update the label (e.g. on variant change)
   * @param {{ silentSelect?: boolean }} [opts] - If silentSelect is true, set the recommended inseam in the DOM but do not dispatch change (avoids triggering a second variant:update)
   */
  function updateInseamRecommendation(variantId, userHeight, allowAutoSelect, opts) {
    if (allowAutoSelect === undefined) allowAutoSelect = true;
    const silentSelect = opts && opts.silentSelect === true;
    if (!loadInseamMappingTable()) {
      return;
    }

    if (!window.inseamMappingTable || !variantId || !userHeight || !hasActualHeight(userHeight)) {
      return;
    }

    const normalizedUserHeight = normalizeHeight(userHeight);
    const variantIdNum = coerceVariantIdToNumber(variantId);
    if (!variantIdNum) return;

    // Find matching entry by variantId and height (first match)
    const match = window.inseamMappingTable.find(function (entry) {
      const normalizedEntryHeight = normalizeHeight(entry.height);
      return entry.variantId === variantIdNum && normalizedEntryHeight === normalizedUserHeight;
    });

    // CRITICAL: Re-query DOM elements fresh (they may have been morphed)
    let inseamFieldset = null;
    let legendKey = null;

    const allLegends = document.querySelectorAll('.variant-option__legend-key[data-variant-option-key]');
    for (let i = 0; i < allLegends.length; i++) {
      const key = allLegends[i].getAttribute('data-variant-option-key');
      if (key && (key.toLowerCase().includes('inseam') || key === 'Inseam:' || key === 'inseam:')) {
        legendKey = allLegends[i];
        inseamFieldset = legendKey.closest('fieldset.variant-option');
        break;
      }
    }

    // If no legend key found, try dropdown case
    if (!legendKey) {
      const allLabels = document.querySelectorAll('.variant-option--dropdowns label');
      for (let i = 0; i < allLabels.length; i++) {
        if (allLabels[i].textContent && allLabels[i].textContent.toLowerCase().includes('inseam')) {
          inseamFieldset = allLabels[i].closest('.variant-option--dropdowns');
          break;
        }
      }
    }

    if (!legendKey && !inseamFieldset) {
      return;
    }

    if (inseamFieldset && inseamFieldset.classList.contains('visually-hidden')) {
      inseamFieldset.classList.remove('visually-hidden');
    }

    // Auto-select recommended inseam only on page load, not on variant change
    if (allowAutoSelect && match && match.inseamRecommended && inseamFieldset) {
      const recommendedNormalized = normalizeInseamForCompare(match.inseamRecommended);
      let currentInseam = null;
      const legendVal = inseamFieldset.querySelector('.variant-option__legend-value');
      if (legendVal) {
        currentInseam = (legendVal.getAttribute('data-variant-option-value') || legendVal.textContent.trim()) || null;
      } else {
        const sel = inseamFieldset.querySelector('select.variant-option__select');
        if (sel && sel.value) currentInseam = sel.value.trim();
      }
      const currentNormalized = normalizeInseamForCompare(currentInseam);
      if (currentNormalized !== recommendedNormalized) {
        const radios = inseamFieldset.querySelectorAll('input[type="radio"]');
        let matchedRadio = null;
        for (let r = 0; r < radios.length; r++) {
          if (normalizeInseamForCompare(radios[r].value) === recommendedNormalized) {
            matchedRadio = radios[r];
            break;
          }
        }
        if (matchedRadio) {
          if (!matchedRadio.checked) {
            matchedRadio.checked = true;
            matchedRadio.setAttribute('data-current-checked', 'true');
            if (!silentSelect) {
              matchedRadio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else {
          const inseamSelect = inseamFieldset.querySelector('select.variant-option__select');
          if (inseamSelect && recommendedNormalized) {
            const options = inseamSelect.querySelectorAll('option');
            for (let o = 0; o < options.length; o++) {
              if (normalizeInseamForCompare(options[o].value) === recommendedNormalized) {
                inseamSelect.value = options[o].value;
                if (!silentSelect) {
                  inseamSelect.dispatchEvent(new Event('change', { bubbles: true }));
                }
                break;
              }
            }
          }
        }
      }
    }

    // Get selected inseam value from DOM (buttons: legend-value; dropdowns: select)
    let selectedInseamValue = null;
    const legendValue = inseamFieldset ? inseamFieldset.querySelector('.variant-option__legend-value') : null;
    if (legendValue) {
      selectedInseamValue = (legendValue.getAttribute('data-variant-option-value') || legendValue.textContent.trim()) || null;
    } else {
      const sel = inseamFieldset ? inseamFieldset.querySelector('select.variant-option__select') : null;
      if (sel && sel.value) selectedInseamValue = sel.value.trim();
    }

    const recommendedStr = normalizeInseamForCompare(match && match.inseamRecommended);
    const selectedStr = normalizeInseamForCompare(selectedInseamValue);
    const selectedMatchesRecommendation = recommendedStr && recommendedStr === selectedStr; // variantAvailable check commented out

    const notRecommendedSpan = inseamFieldset ? inseamFieldset.querySelector('[data-inseam-not-recommended]') : null;
    // Only show when this variant has a mapping row that explicitly says "Inseam not recommended" (case-insensitive)
    const recommendationText = match && match.inseamRecommended != null ? String(match.inseamRecommended).trim() : '';
    const isNotRecommended = match && recommendationText.toLowerCase() === 'inseam not recommended';

    // If user's ShapeLogic size not in product's size range, keep label as "Inseam:" only and hide "(Not recommended)" block
    const sizeInRange = hasMatchingSizeOption();

    // Use current variant availability from DOM (updated on section morph); show "Inseam recommended:" only when variant is available
    const variantPickerForm = document.querySelector('variant-picker[data-template-product-match="true"] .variant-picker__form');
    const variantAvailable = variantPickerForm && variantPickerForm.dataset.variantAvailable === 'true';

    if (notRecommendedSpan) {
      if (!sizeInRange) {
        notRecommendedSpan.classList.add('visually-hidden');
      } else if (isNotRecommended && variantAvailable) {
        notRecommendedSpan.classList.remove('visually-hidden');
        const wrapper = notRecommendedSpan.querySelector('[data-inseam-not-recommended-wrapper]');
        if (wrapper) setupNotRecommendedTooltip(wrapper);
      } else {
        notRecommendedSpan.classList.add('visually-hidden');
      }
    }

    if (legendKey) {
      if (sizeInRange && match && match.inseamRecommended && variantAvailable) {
        if (selectedMatchesRecommendation) {
          legendKey.textContent = 'Inseam recommended:';
          legendKey.setAttribute('data-variant-option-key', 'Inseam recommended:');
        } else {
          legendKey.textContent = 'Inseam:';
          legendKey.setAttribute('data-variant-option-key', 'Inseam:');
        }
      } else {
        legendKey.textContent = 'Inseam:';
        legendKey.setAttribute('data-variant-option-key', 'Inseam:');
      }
    } else if (inseamFieldset) {
      // Dropdown case: update the label text (e.g. "Inseam" -> "Inseam recommended:")
      const dropdownLabel = inseamFieldset.querySelector('label');
      if (dropdownLabel) {
        if (sizeInRange && selectedMatchesRecommendation && variantAvailable) {
          dropdownLabel.textContent = 'Inseam recommended:';
        } else {
          dropdownLabel.textContent = 'Inseam:';
        }
      }
    }
  }

  /**
   * Show quiz-enabled UI elements
   * CRITICAL: Re-queries all DOM elements fresh to handle morphing
   */
  function showQuizEnabledUI() {
    // Show Inseam fieldset - re-query fresh (elements may have been morphed)
    const allLegends = document.querySelectorAll('.variant-option__legend-key[data-variant-option-key]');
    allLegends.forEach(function (legend) {
      const key = legend.getAttribute('data-variant-option-key');
      if (key && (key.toLowerCase().includes('inseam') || key === 'Inseam:' || key === 'inseam:')) {
        const fieldset = legend.closest('fieldset.variant-option');
        if (fieldset && fieldset.classList.contains('visually-hidden')) {
          fieldset.classList.remove('visually-hidden');
        }
      }
    });

    // Also handle dropdown case - re-query fresh
    const allDropdowns = document.querySelectorAll('.variant-option--dropdowns.visually-hidden');
    allDropdowns.forEach(function (dropdown) {
      const label = dropdown.querySelector('label');
      if (label && label.textContent && label.textContent.toLowerCase().includes('inseam')) {
        dropdown.classList.remove('visually-hidden');
      }
    });

    // Show ShapeLogic data block - re-query fresh
    const shapelogicContainer = document.querySelector('.shapelogic-data');
    if (shapelogicContainer && shapelogicContainer.classList.contains('visually-hidden')) {
      shapelogicContainer.classList.remove('visually-hidden');
    }

    const shapelogicElements = document.querySelectorAll('[data-shapelogic-your-size], [data-shapelogic-change], [data-shapelogic-size]');
    shapelogicElements.forEach(function (el) {
      if (el.classList.contains('visually-hidden')) {
        el.classList.remove('visually-hidden');
      }
    });

    // Hide "Get My ShapeLogic Size" button - re-query fresh
    const quizButton = document.querySelector('.user-without--quizdata');
    if (quizButton) {
      quizButton.classList.add('visually-hidden');
    }

    // Show buy button form - re-query fresh
    const buyForm = document.querySelector('.user-with--quizdata');
    if (buyForm) {
      buyForm.classList.remove('visually-hidden', 'disabled');
    }
  }

  /**
   * Get current selected variant ID from variant picker
   * CRITICAL: Re-queries DOM fresh to handle morphing
   */
  function getCurrentVariantId() {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (variantPicker) {
      return variantPicker.dataset.variantId;
    }
    // Fallback: try to get from URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    return variantParam ? parseInt(variantParam) : null;
  }

  /**
   * Returns the currently selected option value id for a given option name.
   * Currently we only rely on this for `color` (buttons/swatches), where the fieldset has:
   * - fieldset.variant-option[data-option-name="color"]
   * - input[type="radio"][data-option-value-id="..."]
   * @param {'color'|'inseam'|'size'|string} optionName
   * @returns {string|null}
   */
  function getSelectedOptionValueIdByName(optionName) {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker || !optionName) return null;

    const fieldset = variantPicker.querySelector(`fieldset.variant-option[data-option-name="${optionName}"]`);
    if (!fieldset) return null;

    const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
    if (!checkedInput) return null;

    return checkedInput.dataset.optionValueId || null;
  }

  /**
   * Returns true if the current variant picker has at least one size option matching the user's ShapeLogic size (fitSymbol).
   * Used to re-apply "Unavailable" state on variant change when the product has no matching size.
   */
  function hasMatchingSizeOption() {
    const fitSymbol = getUserFitSymbol();
    if (!fitSymbol) return true;
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) return true;
    let sizeFieldset = null;
    const allFieldsets = variantPicker.querySelectorAll('fieldset.variant-option');
    for (let i = 0; i < allFieldsets.length; i++) {
      const legend = allFieldsets[i].querySelector('.variant-option__legend-key[data-variant-option-key]');
      if (legend && legend.getAttribute('data-variant-option-key')?.toLowerCase() === 'size') {
        sizeFieldset = allFieldsets[i];
        break;
      }
    }
    if (!sizeFieldset) {
      const allDropdowns = variantPicker.querySelectorAll('.variant-option--dropdowns');
      for (let i = 0; i < allDropdowns.length; i++) {
        const label = allDropdowns[i].querySelector('label');
        if (label && label.textContent && label.textContent.toLowerCase() === 'size') {
          sizeFieldset = allDropdowns[i];
          break;
        }
      }
    }
    if (!sizeFieldset) return true;
    const sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
    for (let i = 0; i < sizeInputs.length; i++) {
      const v = sizeInputs[i].value.trim();
      if (v === fitSymbol || v.startsWith(fitSymbol + '.')) return true;
    }
    const sizeSelect = sizeFieldset.querySelector('select.variant-option__select');
    if (sizeSelect) {
      const optionsList = sizeSelect.querySelectorAll('option');
      for (let i = 0; i < optionsList.length; i++) {
        const v = optionsList[i].value.trim();
        if (v === fitSymbol || v.startsWith(fitSymbol + '.')) return true;
      }
    }
    return false;
  }

  /**
   * Disable add-to-cart and set label to "Unavailable" when user's ShapeLogic size is not in the product's size range.
   * Also visually hides the "(Not recommended)" inseam block.
   */
  function applyNoMatchAddToCartState() {
    const addToCartComponent = document.querySelector('add-to-cart-component');
    if (addToCartComponent && typeof addToCartComponent.disable === 'function') {
      addToCartComponent.disable();
      const addToCartIcon = addToCartComponent.querySelector('.add-to-cart-icon');
      if (addToCartIcon) addToCartIcon.classList.add('hidden');
      const addToCartText = addToCartComponent.querySelector('.add-to-cart-text__content');
      if (addToCartText) addToCartText.innerHTML = 'Unavailable';
    }
    const notRecommendedEl = document.querySelector('[data-inseam-not-recommended]');
    if (notRecommendedEl) notRecommendedEl.classList.add('visually-hidden');
  }

  /**
   * Auto-select size option based on ShapeLogic size from quiz
   * @param {{ skipDispatch?: boolean }} [options] - If skipDispatch is true, set the size option but do not dispatch change (caller will dispatch once)
   * @returns {{ changed: boolean, noMatch: boolean, element?: HTMLInputElement|HTMLSelectElement }} - changed: we selected an option; noMatch: no size matched; element: the input/select to dispatch on if skipDispatch was used
   */
  function autoSelectSizeOption(options) {
    const skipDispatch = options && options.skipDispatch === true;
    const result = { changed: false, noMatch: false };

    const fitSymbol = getUserFitSymbol();
    if (!fitSymbol) {
      return result;
    }

    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) {
      return result;
    }

    const allFieldsets = variantPicker.querySelectorAll('fieldset.variant-option');
    let sizeFieldset = null;

    for (let i = 0; i < allFieldsets.length; i++) {
      const legend = allFieldsets[i].querySelector('.variant-option__legend-key[data-variant-option-key]');
      if (legend) {
        const key = legend.getAttribute('data-variant-option-key');
        if (key && key.toLowerCase() === 'size') {
          sizeFieldset = allFieldsets[i];
          break;
        }
      }
    }

    if (!sizeFieldset) {
      const allDropdowns = variantPicker.querySelectorAll('.variant-option--dropdowns');
      for (let i = 0; i < allDropdowns.length; i++) {
        const label = allDropdowns[i].querySelector('label');
        if (label && label.textContent && label.textContent.toLowerCase() === 'size') {
          sizeFieldset = allDropdowns[i];
          break;
        }
      }
    }

    if (!sizeFieldset) {
      return result;
    }

    // Try to find matching size option (radio button for buttons style)
    const sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
    let matchedInput = null;

    for (let i = 0; i < sizeInputs.length; i++) {
      const inputValue = sizeInputs[i].value.trim();
      // Match exact value or if inputValue starts with fitSymbol followed by a dot
      // e.g., fitSymbol="22.2" matches "22.2", "22.21", "22.22", etc.
      if (inputValue === fitSymbol || inputValue.startsWith(fitSymbol + '.')) {
        matchedInput = sizeInputs[i];
        break;
      }
    }

    if (!matchedInput) {
      const sizeSelect = sizeFieldset.querySelector('select.variant-option__select');
      if (sizeSelect) {
        const optionsList = sizeSelect.querySelectorAll('option');
        for (let i = 0; i < optionsList.length; i++) {
          const optionValue = optionsList[i].value.trim();
          if (optionValue === fitSymbol || optionValue.startsWith(fitSymbol + '.')) {
            sizeSelect.value = optionValue;
            result.changed = true;
            result.element = sizeSelect;
            if (!skipDispatch) {
              sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return result;
          }
        }
      }
      result.noMatch = true;
      applyNoMatchAddToCartState();
      return result;
    }

    if (matchedInput.checked) {
      return result;
    }

    matchedInput.checked = true;
    matchedInput.setAttribute('data-current-checked', 'true');
    result.changed = true;
    result.element = matchedInput;
    if (!skipDispatch) {
      matchedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return result;
  }

  /**
   * Store user height and ShapeLogic size on the variant-picker element for other scripts/DOM use
   */
  function setVariantPickerQuizDataAttributes(userHeight, shapelogicSize) {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (variantPicker) {
      variantPicker.setAttribute('data-user-height', userHeight || '');
      variantPicker.setAttribute('data-shapelogic-size', shapelogicSize || '');
      const hasFit = shapelogicSize != null && String(shapelogicSize).trim() !== '';
      variantPicker.setAttribute('data-quiz-completed', hasFit ? 'true' : 'false');
    }
  }

  /**
   * Toggle loading state on the main variant picker
   */
  function setVariantPickerLoading(isLoading) {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) return;

    if (isLoading) {
      variantPicker.classList.add('variant-picker--loading');
    } else {
      variantPicker.classList.remove('variant-picker--loading');
    }
  }

  /**
   * Main function to check quiz data and update state
   */
  function checkQuizDataAndUpdateState() {
    const userHeight = getUserHeight();
    const userFitSymbol = getUserFitSymbol();
    const hasHeight = hasActualHeight(userHeight);

    setVariantPickerQuizDataAttributes(userHeight, userFitSymbol);

    if (!isInseamFeatureEnabledOnThisProduct()) {
      return;
    }
    setVariantPickerLoading(true);

    if (!userFitSymbol) {
      setVariantPickerLoading(false);
      return;
    }

    // Klaviyo / partial data: show size badge + inseam picker; skip recommendation without actual height
    if (!hasHeight) {
      const sizeResult = autoSelectSizeOption({ skipDispatch: true });
      if (sizeResult.changed && sizeResult.element) {
        window._mmInseamSelfTriggeredVariantUpdate = true;
        sizeResult.element.dispatchEvent(new Event('change', { bubbles: true }));
      }
      applyPartialFitSymbolUI();
      setVariantPickerLoading(false);
      return;
    }

    hideRetakeQuizUI();

    if (!loadInseamMappingTable()) {
      setVariantPickerLoading(false);
      return;
    }

    const currentVariantId = getCurrentVariantId();
    // Initialize tracker on first quiz render so the next Color change can be detected.
    _mmLastSelectedColorValueId = getSelectedOptionValueIdByName('color');

    // Auto-select size first (without dispatching); then dispatch once so variant:update fires only once.
    const sizeResult = autoSelectSizeOption({ skipDispatch: true });

    if (sizeResult.changed && sizeResult.element) {
      window._mmInseamSelfTriggeredVariantUpdate = true;
      sizeResult.element.dispatchEvent(new Event('change', { bubbles: true }));
      showQuizEnabledUI();
      setVariantPickerLoading(false);
      // Inseam will be set in handleVariantUpdate when variant:update fires (with silentSelect to avoid a second dispatch)
    } else {
      if (currentVariantId) {
        updateInseamRecommendation(currentVariantId, userHeight, true);
      }
      showQuizEnabledUI();
      setVariantPickerLoading(false);
    }
  }

  /**
   * Handle variant update event - re-initialize everything after morphing
   * Uses variant ID from event.detail.resource.id (the variant that triggered the update) so the correct variant is matched.
   */
  function handleVariantUpdate(event) {
    if (!isInseamFeatureEnabledOnThisProduct()) {
      // If a previous flow set this flag, ensure it doesn't remain stuck.
      if (window._mmInseamSelfTriggeredVariantUpdate === true) {
        window._mmInseamSelfTriggeredVariantUpdate = false;
      }
      return;
    }
    const selfTriggered = window._mmInseamSelfTriggeredVariantUpdate === true;
    const variantIdFromEvent = event && event.detail && event.detail.resource != null && event.detail.resource.id != null
      ? String(event.detail.resource.id)
      : null;

    if (selfTriggered) {
      window._mmInseamSelfTriggeredVariantUpdate = false;
      const runInseam = function () {
        const userHeight = getUserHeight();
        const userFitSymbol = getUserFitSymbol();
        setVariantPickerQuizDataAttributes(userHeight, userFitSymbol);
        if (!loadInseamMappingTable()) return;
        const variantId = variantIdFromEvent || getCurrentVariantId();
        if (variantId && userHeight && hasActualHeight(userHeight)) {
          /*
           Use silentSelect: false so the inseam change triggers a full update for the correct Size+Inseam variant.
           This ensures the UI and selected variant sync correctly.
          */
          updateInseamRecommendation(variantId, userHeight, true, { silentSelect: false });
        } else if (hasPartialFitData()) {
          applyPartialFitSymbolUI();
        }
      };
      requestAnimationFrame(function () {
        requestAnimationFrame(runInseam);
      });
      return;
    }

    setVariantPickerLoading(true);
    setTimeout(function () {
      const userHeight = getUserHeight();
      const userFitSymbol = getUserFitSymbol();
      setVariantPickerQuizDataAttributes(userHeight, userFitSymbol);

      if (!loadInseamMappingTable()) {
        setVariantPickerLoading(false);
        return;
      }

      const variantId = variantIdFromEvent || getCurrentVariantId();
      const currentColorValueId = getSelectedOptionValueIdByName('color');
      const isColorOptionChange = Boolean(
        currentColorValueId &&
        _mmLastSelectedColorValueId &&
        currentColorValueId !== _mmLastSelectedColorValueId
      );
      // Update tracker immediately so inseam-triggered variant:update won't be mistaken as a Color change.
      if (currentColorValueId) _mmLastSelectedColorValueId = currentColorValueId;

      setTimeout(function () {
        if (variantId && hasActualHeight(getUserHeight())) {
          updateInseamRecommendation(variantId, userHeight, isColorOptionChange);
        } else if (hasPartialFitData()) {
          applyPartialFitSymbolUI();
        }
        if (!hasMatchingSizeOption()) {
          applyNoMatchAddToCartState();
        }
        setVariantPickerLoading(false);
      }, 200);
    }, 100);
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkQuizDataAndUpdateState);
  } else {
    checkQuizDataAndUpdateState();
  }

  // Handle back/forward cache
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      checkQuizDataAndUpdateState();
    }
  });

  // Handle theme editor section loads
  document.addEventListener('shopify:section:load', checkQuizDataAndUpdateState);

  // CRITICAL: Listen for variant update events (fires AFTER morphing completes as it DOM is refreshed)
  document.addEventListener('variant:update', handleVariantUpdate);
})();

